import React, { useEffect, useRef, useState } from 'react';
import styles from './MatchAutoSuggest.module.scss';
import { debounce } from 'lodash';
//redux
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { RootState } from '../../../store';
import { matchesActions } from '../../../store/matches/actions';
//types
import { MatchFromAPI } from '../interfaces/MatchFromAPI';
//utils

import { MatchSuggestionItem } from './MatchSuggestionItem';
import { InputContainer } from '../shared/InputContainer/InputContainer';
import { MatchForecast } from '../interfaces/tip/MatchForecast';
import { Validity } from '../interfaces/tip/Validations';
import { getViewports } from './getViewports';
import { tr } from '../../../utils/translations/translator';
import { useTranslation } from 'react-i18next';
import { MatchSuggestionTitle } from './MatchSuggestionTitle';

interface StateProps {
  placeholder?: string;
  matches: MatchFromAPI[];
  isLoading: boolean;
  handleChange: (v: MatchFromAPI) => void;
  onDelete: () => void;
  label: string;
  matchForecast: MatchForecast;
  validity?: Validity | false;
}

interface DispatchProps {
  onSearch: (search: string) => void;
  clearSearch: () => void;
}

type OwnProps = StateProps & DispatchProps;

const debounceFetchRequest = debounce(
  (onSearch: (search: string) => void, search: string) => {
    onSearch(search);
  },
  100,
);

const MatchAutoSuggest: React.FC<OwnProps> = ({
  placeholder,
  matches,
  onSearch,
  onDelete,
  clearSearch,
  handleChange,
  label,
  matchForecast,
  validity,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(
    matchForecast.home_player
      ? `${matchForecast.home_player} ${
          matchForecast.away_player && '- ' + matchForecast.away_player
        }`
      : '',
  );

  const [showDelete, setShowDelete] = useState(!!matchForecast.match_id);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const elementRef = useRef<HTMLInputElement>(null);

  const onChange = (e: any) => {
    debounceFetchRequest.cancel();
    if (!!e.target.value.length) {
      debounceFetchRequest(onSearch, e.target.value);
      setIsDropdownOpen(true);
    } else {
      setIsDropdownOpen(false);
    }
    setValue(e.target.value);
  };

  const getSuggestionValue = (matchForecast: MatchFromAPI) => {
    return `${matchForecast.home_player} ${
      matchForecast.away_player && '- ' + matchForecast.away_player
    }`;
  };
  const handleSelect = (matchForecast: MatchFromAPI) => {
    handleChange(matchForecast);
    setValue(getSuggestionValue(matchForecast));
    setShowDelete(true);
    setIsDropdownOpen(false);
  };

  const handleDelete = () => {
    setValue('');
    setIsDropdownOpen(false);
    setShowDelete(false);
    clearSearch();
    onDelete();
  };

  const onBlur = () => {
    setTimeout(() => {
      //delay to ensure onBlur does't trigger before updates
      if (!matchForecast.match_id) {
        handleDelete();
        setShowDelete(false);
      }
    }, 500);
  };

  const handleResize = () => {
    const { viewport, visualViewport } = getViewports();

    elementRef &&
      elementRef.current &&
      setKeyboardHeight(
        viewport.height -
          (viewport.height - visualViewport.height) -
          elementRef.current.clientHeight,
      );
  };

  useEffect(() => {
    window.visualViewport &&
      window.visualViewport.addEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleResize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementRef.current, matches.length, isLoading]);

  return (
    <>
      <InputContainer label={label} validity={validity}>
        <div className={styles.autosuggestWrapper}>
          <input
            className={styles.input}
            onChange={onChange}
            value={value}
            onBlur={onBlur}
            placeholder={placeholder}
          ></input>

          {showDelete && (
            <button
              className={styles.deleteBtn}
              onClick={() => {
                handleDelete();
                setShowDelete(false);
              }}
            >
              ✕
            </button>
          )}
          {!!matches.length && !isLoading && isDropdownOpen && (
            <>
              <div
                className={styles.hiddenScrollable}
                style={{ height: `${keyboardHeight}px` }}
                onClick={() => setIsDropdownOpen(false)}
              >
                <div className={styles.innerScrollable}></div>
              </div>
              <div
                className={styles.autoSuggestContainer}
                style={{ top: `${keyboardHeight}px` }}
                ref={elementRef}
              >
                <ul
                  className={styles.dropdownHeader}
                  onClick={() => setIsDropdownOpen(false)}
                >
                  <MatchSuggestionTitle
                    title={tr(t, 'match-dropdown_title')}
                  ></MatchSuggestionTitle>
                </ul>

                {matches.map((match) => (
                  <MatchSuggestionItem
                    key={Math.random()}
                    match={match}
                    onClick={() => handleSelect(match)}
                  />
                ))}
              </div>
            </>
          )}
          {!!value.length &&
            matches.length === 0 &&
            !matchForecast.match_id &&
            !isLoading &&
            isDropdownOpen && (
              <>
                <div
                  className={styles.hiddenScrollable}
                  style={{ height: `${keyboardHeight}px` }}
                >
                  <div className={styles.innerScrollable}></div>
                </div>

                <div
                  className={styles.autoSuggestContainer}
                  id={styles.emptyContainer}
                >
                  <div
                    className={styles.autoSuggestContainer}
                    style={{ top: `${keyboardHeight}px` }}
                    ref={elementRef}
                  >
                    <ul className={styles.dropdownHeader}>
                      <MatchSuggestionTitle
                        title={tr(t, 'match-not-found')}
                        type={'emptyState'}
                      ></MatchSuggestionTitle>
                    </ul>
                  </div>
                </div>
              </>
            )}
        </div>
      </InputContainer>
    </>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onSearch: (search: string) =>
    dispatch(matchesActions.searchMatchesRequestAction(search)),
  clearSearch: () => {
    dispatch(matchesActions.removeSearch());
  },
});

const mapStateToProps = (state: RootState) => ({
  matches: state.matches.search.data,
  isLoading: state.matches.search.isLoading,
});

export const MatchAutoSuggestConnected = connect(
  mapStateToProps,
  mapDispatchToProps,
)(MatchAutoSuggest);
